import { useEffect, useRef } from "react";
import {
  useFetchOrganizationInvitations,
  useCreateOrganizationInvitation,
} from "@core/features/organization";
import {
  INVITATION_TYPE,
  useFetchInvitationTemplates,
} from "@core/features/invitation";
import { Loader } from "@themes/pollinator/ui/Loader";
import { OrgTrackingTab } from "./OrgTrackingTab";

type Props = {
  organizationId: number;
  programId: number;
};

/**
 * Container for OrgTrackingTab that fetches invitations.
 * Filters to get participant invitations for the tracking tab.
 * Auto-creates participant invitation if none exists.
 */
export function OrgTrackingTabContainer({ organizationId, programId }: Props) {
  const hasAttemptedCreate = useRef(false);

  const { data: invitations, isLoading } = useFetchOrganizationInvitations({
    organizationId,
  });

  const { data: templates, isLoading: isLoadingTemplates } =
    useFetchInvitationTemplates({
      programId,
      type: INVITATION_TYPE.PARTICIPANT,
    });

  const { mutate: createInvitation } = useCreateOrganizationInvitation({
    organizationId,
  });

  // Filter for participant invitations
  const participantInvitations =
    invitations?.filter((inv) => inv.type === INVITATION_TYPE.PARTICIPANT) ??
    [];

  const hasParticipantInvitation = participantInvitations.length > 0;

  // Get the first participant template
  const participantTemplate = templates?.[0] ?? null;

  // Auto-create participant invitation if none exists and template is available
  useEffect(() => {
    if (
      !isLoading &&
      !isLoadingTemplates &&
      invitations &&
      !hasParticipantInvitation &&
      participantTemplate &&
      !hasAttemptedCreate.current
    ) {
      hasAttemptedCreate.current = true;
      createInvitation({
        programId,
        templateId: participantTemplate.id,
        type: INVITATION_TYPE.PARTICIPANT,
      });
    }
  }, [
    isLoading,
    isLoadingTemplates,
    invitations,
    hasParticipantInvitation,
    participantTemplate,
    createInvitation,
    programId,
  ]);

  if (
    isLoading ||
    isLoadingTemplates ||
    (!hasParticipantInvitation && !hasAttemptedCreate.current)
  ) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <OrgTrackingTab
      invitations={participantInvitations}
      organizationId={organizationId}
      programId={programId}
      templateId={participantTemplate?.id ?? null}
    />
  );
}
